import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { format } from 'date-fns'

import { DatePicker, DateRangePicker, DatesData } from '@dfds-pax/shared-components/client'
import { getDatepickerLocale } from '@dfds-pax/utils'

import { useTravelDates } from '../../Hooks/dates/useTravelDates'
import { useRoutes } from '../../Hooks/routes/useRoutes'

import { ActiveSelectField, useActiveSelectField } from '../../context/ActiveSelectFieldProvider'
import { useTravelSearchContext } from '../../context/TravelSearchProvider'
import { getLocaleFromUrlOrQuery } from '../../Utils/common'
import DateField from './DateField'

const useHardcodedDatePickerData = false

const hardcodedOutboundDates: DatesData = {
  dates: {
    fromDate: '2030-07-01',
    toDate: '2030-09-30',
  },
  defaultDate: '2030-07-05',
  disabledDates: ['2030-07-22', '2030-07-23'],
  offerDates: [],
}

const hardcodedReturnDates: DatesData = {
  dates: {
    fromDate: '2030-07-01',
    toDate: '2030-09-30',
  },
  defaultDate: '2030-07-06',
  disabledDates: ['2030-07-18', '2030-07-20', '2030-07-27'],
  offerDates: [],
}

const DateSelector = () => {
  const { t } = useTranslation()
  const [returnFocused, setReturnFocused] = useState(false)
  const selectorContainer = useRef<HTMLDivElement>(null as unknown as HTMLDivElement)
  const dateTriggerRef = useRef<HTMLButtonElement>(null)

  const locale = getLocaleFromUrlOrQuery()

  const { routesApiData } = useRoutes()
  const { homeboundTravelDates, isTravelDatesLoading, outboundTravelDates, selectedHomeboundDate, selectedOutboundDate } = useTravelDates()
  const {
    dispatch,
    state: { isOneWay },
  } = useTravelSearchContext()
  const { activeField, closeIfActive, toggleField } = useActiveSelectField()

  const isOpen = activeField === ActiveSelectField.DATE
  const datePickerOutboundDates = useHardcodedDatePickerData ? hardcodedOutboundDates : outboundTravelDates
  const datePickerReturnDates = useHardcodedDatePickerData ? hardcodedReturnDates : homeboundTravelDates

  // Determine if fields should be disabled
  const isDisabled = useMemo(() => {
    if (!outboundTravelDates) return false
    if (isOneWay) {
      return outboundTravelDates.dates.fromDate === outboundTravelDates.dates.toDate
    }
    if (!homeboundTravelDates) return false
    return (
      outboundTravelDates.dates.fromDate === outboundTravelDates.dates.toDate &&
      homeboundTravelDates.dates.fromDate === homeboundTravelDates.dates.toDate
    )
  }, [isOneWay, outboundTravelDates, homeboundTravelDates])

  const dataAttrList = useMemo(() => {
    return [
      {
        name: 'selected-outbound-date',
        value: selectedOutboundDate ? format(selectedOutboundDate, 'yyyy-MM-dd') : '',
      },
      {
        name: 'selected-homebound-date',
        value: selectedHomeboundDate ? format(selectedHomeboundDate, 'yyyy-MM-dd') : '',
      },
    ]
  }, [selectedOutboundDate, selectedHomeboundDate])

  // Handle the click that opens or closes the date picker
  const handleClick = useCallback(
    (clickedOutside: boolean) => {
      if (isDisabled) return
      setReturnFocused(false)
      if (!isTravelDatesLoading) {
        toggleField(ActiveSelectField.DATE, clickedOutside)
      }
    },
    [isDisabled, isTravelDatesLoading, toggleField]
  )

  const handleOutboundChange = useCallback(
    (date: Date) => {
      if (!isOneWay && routesApiData?.offerOrProductCode) {
        const tempHomeboundDateData: DatesData = {
          dates: {
            fromDate: date.toString(),
            toDate: date.toString(),
          },
          defaultDate: date.toString(),
          disabledDates: [],
          offerDates: [],
        }
        dispatch({
          payload: tempHomeboundDateData,
          type: 'SET_HOMEBOUND_TRAVEL_DATES',
        })
        dispatch({
          dates: { homeboundDate: date, outboundDate: date },
          type: 'SET_DATES',
        })
      } else {
        dispatch({
          dates: {
            homeboundDate: isOneWay ? undefined : date,
            outboundDate: date,
          },
          type: 'SET_DATES',
        })
      }

      if (isOneWay) {
        closeIfActive(ActiveSelectField.DATE)
        dateTriggerRef.current?.focus()
      } else {
        setReturnFocused(true)
      }
    },
    [isOneWay, routesApiData, dispatch, closeIfActive]
  )

  const handleReturnChange = useCallback(
    (date: Date) => {
      dispatch({
        dates: { homeboundDate: date, outboundDate: selectedOutboundDate },
        type: 'SET_DATES',
      })
      setReturnFocused(false)
      closeIfActive(ActiveSelectField.DATE)
      dateTriggerRef.current?.focus()
    },
    [dispatch, selectedOutboundDate, closeIfActive]
  )

  return (
    <div className="col-span-12 box-border h-[58px] w-full md:col-span-6 lg:basis-[350px]" ref={selectorContainer}>
      <DateField
        dataAttrList={dataAttrList}
        disabled={isDisabled}
        fieldName="date"
        isLoading={isTravelDatesLoading}
        isOpen={isOpen}
        onClick={handleClick}
        onClose={() => {
          setReturnFocused(false)
          closeIfActive(ActiveSelectField.DATE)
        }}
        showFooter={false}
        triggerRef={dateTriggerRef}
      >
        {selectedOutboundDate && isOneWay ? (
          <DatePicker
            isShownInDrawer={isOpen}
            locale={getDatepickerLocale(locale)}
            onDateChanged={(date) => date && handleOutboundChange(date)}
            selectedDate={selectedOutboundDate}
            datesData={outboundTravelDates}
            offerLabel={t('SPECIAL-OFFER-AVAILABLE')}
          />
        ) : null}
        {!isOneWay && datePickerOutboundDates ? (
          <DateRangePicker
            isShownInDrawer={isOpen}
            selectedRange={
              selectedOutboundDate && returnFocused
                ? { from: selectedOutboundDate }
                : selectedOutboundDate && selectedHomeboundDate
                  ? { from: selectedOutboundDate, to: selectedHomeboundDate }
                  : undefined
            }
            returnDatesData={datePickerReturnDates}
            locale={getDatepickerLocale(locale)}
            onDateChanged={(date) => {
              if (date?.from && (!returnFocused || !date.to)) {
                handleOutboundChange(date.from)
              }
              if (returnFocused && date?.to) {
                handleReturnChange(date.to)
              }
            }}
            outboundDatesData={datePickerOutboundDates}
            offerLabel={t('SPECIAL-OFFER-AVAILABLE')}
          />
        ) : null}
      </DateField>
    </div>
  )
}
export default DateSelector
